import {Component, Input, ChangeDetectionStrategy} from '@angular/core';

import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faCircleCheck,
  faFile,
  faFileLines,
  faShieldCheck,
} from '@fortawesome/pro-solid-svg-icons';
import {faSpinnerThird} from '@fortawesome/pro-duotone-svg-icons';
import {ExecutionStep, LogEntry, ProgressInfo, SelectedFileSummary, SelectedRuleSummary} from './step-processing.model';

@Component({
  selector: 'my-checks-step-processing',
  standalone: true,
  imports: [FaIconComponent],
  templateUrl: './step-processing.component.html',
  styleUrls: ['./step-processing.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {class: 'flex flex-col h-full w-full overflow-hidden'},
})
export class StepProcessingComponent {
  @Input() steps: ExecutionStep[] = [];
  @Input() logs: LogEntry[] = [];
  @Input() progress: ProgressInfo = {
    currentStep: 0,
    totalSteps: 0,
    elapsedSeconds: 0,
    percentage: 0,
    checkName: 'Prüfung',
  };
  /** Selected rule summaries passed from the execution component. */
  @Input() selectedRules: SelectedRuleSummary[] = [];
  /** Selected file summaries passed from the execution component. */
  @Input() selectedFiles: SelectedFileSummary[] = [];
  /** Current status message (e.g. loading message from the execution step). */
  @Input() statusMessage = '';

  /** Namen der Prüfregelsets, deren Detail-Box AUFgeklappt ist (Rest ist per Default eingeklappt). */
  private expandedRulesetNames = new Set<string>();
  /** Dateitypen, deren Detail-Box AUFgeklappt ist (Rest ist per Default eingeklappt). */
  private expandedFileTypes = new Set<string>();

  protected readonly faCircleCheck = faCircleCheck;
  protected readonly faFile = faFile;
  protected readonly faFileLines = faFileLines;
  protected readonly faChevronDown = faChevronDown;
  protected readonly faChevronUp = faChevronUp;
  protected readonly faShieldCheck = faShieldCheck;
  protected readonly faSpinnerThird = faSpinnerThird;

  get progressPercentage(): number {
    return Math.min(100, Math.max(0, this.progress.percentage));
  }

  /**
   * Die echten Zahlen des Laufs ("148 von 220 Einzelprüfungen"), oder `null`, solange das Backend
   * keinen Einheiten-Plan liefert (Altläufe). Eine Einheit ist eine Datei × eine Prüfregel.
   *
   * Bewusst als Zahlenpaar und nicht als fertiger Satz: Im Template werden nur die Zahlen
   * hervorgehoben, die Wörter bleiben normal – wie bei "Dauer" und "Gestartet" in derselben Zeile.
   *
   * Bis zu dieser Anzeige existierte die Zahl ausschließlich als Log-Zeile im Backend
   * ("KI-Fan-In wartet: terminal=40/220").
   */
  get unitProgress(): { done: number; total: number } | null {
    const total = this.progress.unitsTotal;
    if (!total || total <= 0) return null;
    return {done: Math.min(this.progress.unitsDone ?? 0, total), total};
  }

  /** Anzahl fehlgeschlagener Einzelprüfungen (0, wenn keine fehlgeschlagen ist). */
  get unitFailedCount(): number {
    return this.progress.unitsFailed ?? 0;
  }

  /**
   * Die feinere zweite Zahl ("12 von 40 Räumen"), oder `null`, solange die laufende Prüfregel nicht
   * elementweise auffächert.
   *
   * Warum überhaupt eine zweite Zeile: Eine hybride Regel über ein Raumbuch ist im Einheitenplan
   * genau *eine* Einheit. Ohne diese Zahl stand der Balken minutenlang still, während im Hintergrund
   * 40 Räume nacheinander geprüft wurden – und war damit von "hängt" nicht zu unterscheiden.
   *
   * Das Label kommt aus dem Backend (`elementLabel`), damit die Anzeige keine Fachlogik über
   * Prüfebenen führt.
   */
  get elementProgress(): { done: number; total: number; label: string } | null {
    const total = this.progress.elementUnitsTotal;
    if (!total || total <= 0) return null;
    return {
      done: Math.min(this.progress.elementUnitsDone ?? 0, total),
      total,
      label: this.progress.elementLabel || 'Elementen',
    };
  }

  get elapsedFormatted(): string {
    const mins = Math.floor(this.progress.elapsedSeconds / 60);
    const secs = this.progress.elapsedSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  get rulesetCount(): number {
    return this.selectedRules.length;
  }

  get ruleCount(): number {
    return this.selectedRules.reduce((sum, r) => sum + this.rulesInSet(r), 0);
  }

  /**
   * Regelanzahl eines Regelsets: die Einzelnamen, sonst die mitgelieferte Anzahl.
   *
   * Die Namen fehlen, wenn die Auswahl nach einem Reload aus dem Instanz-State rekonstruiert wurde
   * und dort ein ganzes IDS-/HYBRID-Set als eine Pseudo-Regel steht – dann traegt
   * {@link SelectedRuleSummary.ruleCount} die echte Zahl.
   */
  rulesInSet(ruleset: SelectedRuleSummary): number {
    return ruleset.ruleNames?.length ?? ruleset.ruleCount ?? 1;
  }

  get fileCount(): number {
    return this.selectedFiles.length;
  }

  get totalFileSizeFormatted(): string {
    const totalBytes = this.selectedFiles.reduce((sum, f) => sum + (f.size ?? 0), 0);
    return this.formatBytes(totalBytes);
  }

  get filesByType(): { type: string; label: string; files: SelectedFileSummary[]; totalSize: number }[] {
    const groups = new Map<string, SelectedFileSummary[]>();
    for (const file of this.selectedFiles) {
      const key = file.fileType ?? 'OTHER';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(file);
    }
    return Array.from(groups.entries()).map(([type, files]) => ({
      type,
      label: this.getFileTypeLabel(type),
      files,
      totalSize: files.reduce((sum, f) => sum + (f.size ?? 0), 0),
    }));
  }

  /** True, wenn KEIN Prüfregelset aufgeklappt ist – steuert das Kopf-Chevron (Alle auf-/zuklappen). */
  get allRulesetsCollapsed(): boolean {
    return this.selectedRules.length > 0
      && this.selectedRules.every(r => !this.expandedRulesetNames.has(r.name));
  }

  /** True, wenn KEINE Dateikategorie aufgeklappt ist – steuert das Kopf-Chevron (Alle auf-/zuklappen). */
  get allFileTypesCollapsed(): boolean {
    const groups = this.filesByType;
    return groups.length > 0 && groups.every(g => !this.expandedFileTypes.has(g.type));
  }

  /** Kopf-Chevron: alles auf- oder zuklappen. Arbeitet auf demselben Set wie die Einzel-Toggles,
   *  damit sich einzelne Gruppen immer unabhängig auf-/zuklappen lassen (kein zweiter Gate-Flag). */
  toggleRules(): void {
    if (this.allRulesetsCollapsed) {
      this.selectedRules.forEach(r => this.expandedRulesetNames.add(r.name));
    } else {
      this.expandedRulesetNames.clear();
    }
  }

  toggleFiles(): void {
    if (this.allFileTypesCollapsed) {
      this.filesByType.forEach(g => this.expandedFileTypes.add(g.type));
    } else {
      this.expandedFileTypes.clear();
    }
  }

  isRulesetCollapsed(rulesetName: string): boolean {
    return !this.expandedRulesetNames.has(rulesetName);
  }

  toggleRulesetCollapse(rulesetName: string): void {
    if (this.expandedRulesetNames.has(rulesetName)) {
      this.expandedRulesetNames.delete(rulesetName);
    } else {
      this.expandedRulesetNames.add(rulesetName);
    }
  }

  isFileTypeCollapsed(fileType: string): boolean {
    return !this.expandedFileTypes.has(fileType);
  }

  toggleFileTypeCollapse(fileType: string): void {
    if (this.expandedFileTypes.has(fileType)) {
      this.expandedFileTypes.delete(fileType);
    } else {
      this.expandedFileTypes.add(fileType);
    }
  }

  getBadgeClass(type: string): string {
    switch (type?.toUpperCase()) {
      case 'HYBRID':
        return 'bg-[#68828C] text-white';
      case 'KI':
        return 'bg-[#CDD5D9] text-gray-700';
      default:
        return 'bg-gray-200 text-gray-700';
    }
  }

  getRuleTypeLabel(type: string): string {
    switch (type?.toUpperCase()) {
      case 'HYBRID':
        return 'Hybride Prüfung';
      case 'KI':
        return 'KI Prüfung';
      case 'IDS':
        return 'IDS Prüfung';
      default:
        return type;
    }
  }

  /**
   * Beschriftung des Teillauf-Status eines Regelsets – nur bei gemischten Läufen gesetzt
   * (ein Sub-Run je Regelset). {@code null} = kein eigener Status, nichts anzeigen.
   */
  getRulesetStatusLabel(runStatus?: string): string | null {
    switch (runStatus) {
      case 'completed':
        return 'Abgeschlossen';
      case 'running':
        return 'Läuft';
      case 'failed':
        return 'Fehlgeschlagen';
      case 'pending':
        return 'Wartet';
      default:
        return null;
    }
  }

  getRulesetStatusClass(runStatus?: string): string {
    switch (runStatus) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'running':
        return 'bg-blue-100 text-blue-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  }

  getFileIcon(fileType?: string): any {
    switch (fileType?.toUpperCase()) {
      case 'PDF':
        return this.faFileLines;
      default:
        return this.faFile;
    }
  }

  formatGroupSize(bytes: number): string {
    return this.formatBytes(bytes);
  }

  private formatBytes(bytes: number): string {
    if (!bytes || bytes === 0) return '–';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  }

  private getFileTypeLabel(type: string): string {
    switch (type?.toUpperCase()) {
      case 'IFC':
        return 'IFC-Modelle';
      case 'PDF':
        return 'PDF Dokumente';
      case 'XLSX':
      case 'XLS':
        return 'Excel-Dateien';
      default:
        return `${type}-Dateien`;
    }
  }
}
